import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import Navbar from '../components/Navbar';
import { StatsOverview } from '../components/admin/StatsOverview';
import { DocumentReviewSection } from '../components/admin/DocumentReviewSection';
import { NotificationSection } from '../components/admin/NotificationSection';
import { ResourceManagementSection } from '../components/admin/ResourceManagementSection';
import { AffiliateApplicationsSection } from '../components/admin/AffiliateApplicationsSection';
import { ServiceProviderApplicationsSection } from '../components/admin/ServiceProviderApplicationsSection';
import {
  Users, ShieldCheck, Trash2, Plus, Search, ChevronDown, ChevronUp,
  Truck, Building2, Headphones, Edit3, X, Check, RefreshCw,
  FileText, Crown, Bell, BookOpen, Download, ArrowUpDown, MessageSquare, Send, Award, Wrench,
  ImagePlus, Film
} from 'lucide-react';
import { toast } from 'sonner';
import MentionInput from '../components/MentionInput';
import PostContent from '../components/PostContent';
import { mediaUrl } from '../utils/media';

const ROLE_ICONS = { driver: Truck, carrier_owner: Building2, dispatcher: Headphones, learner: BookOpen, affiliate: Award, service_provider: Wrench, admin: Crown };
const ROLE_COLORS = { driver: 'text-blue-600 bg-blue-50', carrier_owner: 'text-emerald-600 bg-emerald-50', dispatcher: 'text-purple-600 bg-purple-50', learner: 'text-amber-600 bg-amber-50', affiliate: 'text-orange-600 bg-orange-50', service_provider: 'text-sky-600 bg-sky-50', admin: 'text-amber-600 bg-amber-50' };
const STATUS_COLORS = { approved: 'text-green-700 bg-green-100', verified: 'text-green-700 bg-green-100', pending: 'text-yellow-700 bg-yellow-100', declined: 'text-red-700 bg-red-100', rejected: 'text-red-700 bg-red-100' };

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateSortAsc, setDateSortAsc] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [approvingUserId, setApprovingUserId] = useState(null);
  const [expandedUser, setExpandedUser] = useState(null);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'driver' });
  const [activeTab, setActiveTab] = useState('users');
  const [documents, setDocuments] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [archivedNotifs, setArchivedNotifs] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [communityPosts, setCommunityPosts] = useState([]);
  const [communityLoading, setCommunityLoading] = useState(false);
  const [replyText, setReplyText] = useState({});
  const [exporting, setExporting] = useState(false);
  const [newPostText, setNewPostText] = useState('');
  const [newPostMentions, setNewPostMentions] = useState([]);
  const [newPostMedia, setNewPostMedia] = useState([]);
  const [adminUploading, setAdminUploading] = useState(false);
  const adminFileInputRef = React.useRef(null);
  const [postingNew, setPostingNew] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, usersRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/users')
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
    } catch {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user?.is_admin) { navigate('/dashboard'); return; }
    fetchData();
    const loadNotifCount = async () => {
      try {
        const res = await api.get('/admin/notifications');
        setUnreadCount(res.data.unread_count || 0);
      } catch { /* non-critical */ }
    };
    loadNotifCount();
  }, [user, navigate, fetchData]);

  const fetchDocuments = async () => {
    setDocsLoading(true);
    try {
      const res = await api.get('/admin/documents');
      setDocuments(res.data.documents || []);
    } catch { toast.error('Failed to load documents'); }
    finally { setDocsLoading(false); }
  };

  const handleReviewDocument = async (badgeId, status) => {
    try {
      await api.put(`/admin/documents/${badgeId}`, { status });
      toast.success(`Document ${status}`);
      fetchDocuments();
    } catch { toast.error('Failed to update document'); }
  };

  const fetchNotifications = async () => {
    setNotifLoading(true);
    try {
      const res = await api.get('/admin/notifications');
      setNotifications(res.data.notifications || []);
      setArchivedNotifs(res.data.archived || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch { toast.error('Failed to load notifications'); }
    finally { setNotifLoading(false); }
  };

  const handleMarkRead = async (notifId) => {
    try {
      await api.put(`/admin/notifications/${notifId}/read`);
      fetchNotifications();
    } catch { toast.error('Failed to mark as read'); }
  };

  const fetchCommunityPosts = async () => {
    setCommunityLoading(true);
    try {
      const res = await api.get('/admin/community/posts');
      setCommunityPosts(res.data.posts || []);
    } catch { toast.error('Failed to load community posts'); }
    finally { setCommunityLoading(false); }
  };

  const handleAdminReply = async (postId) => {
    const text = (replyText[postId] || '').trim();
    if (!text) return;
    try {
      await api.post(`/admin/community/posts/${postId}/reply`, { text });
      toast.success('Reply posted');
      setReplyText(prev => ({ ...prev, [postId]: '' }));
      fetchCommunityPosts();
    } catch { toast.error('Failed to post reply'); }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Delete this community post?')) return;
    try {
      await api.delete(`/admin/community/posts/${postId}`);
      toast.success('Post deleted');
      fetchCommunityPosts();
    } catch { toast.error('Failed to delete post'); }
  };

  const handleCreatePost = async () => {
    const text = newPostText.trim();
    if (!text && newPostMedia.length === 0) return;
    setPostingNew(true);
    try {
      const media = newPostMedia.map(m => ({ url: m.url, media_type: m.media_type }));
      await api.post('/admin/community/posts', { content: text, media, mentions: newPostMentions });
      toast.success('Post published');
      setNewPostText('');
      setNewPostMentions([]);
      newPostMedia.forEach(m => { if (m.preview) URL.revokeObjectURL(m.preview); });
      setNewPostMedia([]);
      fetchCommunityPosts();
    } catch { toast.error('Failed to create post'); }
    finally { setPostingNew(false); }
  };

  const handleAdminFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (newPostMedia.length + files.length > 4) {
      toast.error('Maximum 4 files per post');
      return;
    }
    setAdminUploading(true);
    const uploaded = [];
    for (const file of files) {
      if (file.size > 50 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 50MB)`);
        continue;
      }
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await api.post('/community/upload-media', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        uploaded.push({ url: res.data.url, media_type: res.data.media_type, preview: URL.createObjectURL(file) });
      } catch { toast.error(`Failed to upload ${file.name}`); }
    }
    setNewPostMedia(prev => [...prev, ...uploaded]);
    setAdminUploading(false);
    if (adminFileInputRef.current) adminFileInputRef.current.value = '';
  };

  const removeAdminMedia = (i) => {
    setNewPostMedia(prev => {
      const copy = [...prev];
      if (copy[i].preview) URL.revokeObjectURL(copy[i].preview);
      copy.splice(i, 1);
      return copy;
    });
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const res = await api.get('/admin/export/users', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'straightconnect_users.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('User data exported');
    } catch { toast.error('Failed to export data'); }
    finally { setExporting(false); }
  };

  const handleCreateUser = async () => {
    if (!newUser.name || !newUser.email) { toast.error('Name and email required'); return; }
    try {
      await api.post('/admin/users', newUser);
      toast.success('User created');
      setShowCreateModal(false);
      setNewUser({ name: '', email: '', password: '', role: 'driver' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create user'); }
  };

  const handleDeleteUser = async (userId, email) => {
    if (!window.confirm(`Delete user ${email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('User deleted');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete user'); }
  };

  const handleUpdateUser = async (userId, updates) => {
    try {
      await api.put(`/admin/users/${userId}`, updates);
      toast.success('User updated');
      setEditingUser(null);
      fetchData();
    } catch { toast.error('Failed to update user'); }
  };

  const handleQuickApprove = async (userId, userName) => {
    setApprovingUserId(userId);
    try {
      await api.put(`/admin/users/${userId}`, { verification_status: 'approved' });
      toast.success(`${userName} has been approved`);
      fetchData();
    } catch { toast.error('Failed to approve user'); }
    finally { setApprovingUserId(null); }
  };

  const filteredUsers = users
    .filter(u => {
      const matchSearch = u.name?.toLowerCase().includes(searchTerm.toLowerCase()) || u.email?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchRole = roleFilter === 'all' || u.role === roleFilter;
      const vStatus = u.profile?.verification_status || 'pending';
      const matchStatus = statusFilter === 'all' || vStatus === statusFilter;
      return matchSearch && matchRole && matchStatus;
    })
    .sort((a, b) => {
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      return dateSortAsc ? dateA - dateB : dateB - dateA;
    });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950" data-testid="admin-dashboard">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <Crown className="h-7 w-7 text-amber-400" />
              Admin Dashboard
            </h1>
            <p className="text-slate-400 text-sm mt-1">Manage users, verification, and platform settings</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleExportCSV} disabled={exporting}
              className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 hover:text-white px-4 py-2.5 rounded-lg font-medium transition-colors disabled:opacity-50"
              data-testid="export-csv-btn">
              {exporting ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Export CSV
            </button>
            <button onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg font-medium transition-colors"
              data-testid="add-user-btn">
              <Plus className="h-4 w-4" />Add User
            </button>
          </div>
        </div>

        <StatsOverview stats={stats} />

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {[
            { id: 'users', icon: Users, label: 'Users' },
            { id: 'documents', icon: FileText, label: 'Documents', onClick: fetchDocuments },
            { id: 'notifications', icon: Bell, label: 'Notifications', badge: unreadCount, onClick: fetchNotifications },
            { id: 'community', icon: MessageSquare, label: 'Community', onClick: fetchCommunityPosts },
            { id: 'resources', icon: BookOpen, label: 'Resources' },
            { id: 'affiliate', icon: Award, label: 'Affiliate Hub' },
            { id: 'service_provider', icon: Wrench, label: 'Service Provider' },
          ].map(tab => (
            <button key={tab.id}
              onClick={() => { setActiveTab(tab.id); tab.onClick?.(); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === tab.id ? 'bg-blue-600 text-white' : 'bg-white/5 text-slate-400 hover:text-white border border-white/10'}`}
              data-testid={`tab-${tab.id}`}>
              <span className="flex items-center gap-2">
                <tab.icon className="h-4 w-4" />{tab.label}
                {tab.badge > 0 && <span className="bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[20px] text-center">{tab.badge}</span>}
              </span>
            </button>
          ))}
        </div>

        {/* ===== USERS TAB ===== */}
        {activeTab === 'users' && (<>
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input type="text" placeholder="Search by name or email..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="admin-search-input" />
            </div>
            <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}
              className="bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="admin-role-filter">
              <option value="all" className="bg-slate-900">All Roles</option>
              <option value="driver" className="bg-slate-900">Drivers</option>
              <option value="carrier_owner" className="bg-slate-900">Carriers</option>
              <option value="dispatcher" className="bg-slate-900">Dispatchers</option>
              <option value="affiliate" className="bg-slate-900">Affiliate Hub</option>
              <option value="service_provider" className="bg-slate-900">Service Provider</option>
              <option value="learner" className="bg-slate-900">Learn the Trade</option>
              <option value="learner" className="bg-slate-900">Learn the Trade</option>
              <option value="admin" className="bg-slate-900">Admins</option>
            </select>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="admin-status-filter">
              <option value="all" className="bg-slate-900">All Status</option>
              <option value="approved" className="bg-slate-900">Verified</option>
              <option value="pending" className="bg-slate-900">Pending</option>
              <option value="declined" className="bg-slate-900">Declined</option>
            </select>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">User</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Role</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Tier</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
                      <button onClick={() => setDateSortAsc(!dateSortAsc)} className="flex items-center gap-1 hover:text-white transition-colors" data-testid="sort-date-btn">
                        Joined <ArrowUpDown className="h-3 w-3" />
                      </button>
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u) => {
                    const RoleIcon = ROLE_ICONS[u.role] || Users;
                    const roleColor = ROLE_COLORS[u.role] || 'text-slate-600 bg-slate-50';
                    const vStatus = u.profile?.verification_status || 'pending';
                    const statusColor = STATUS_COLORS[vStatus] || STATUS_COLORS.pending;
                    const isExpanded = expandedUser === u.id;

                    return (
                      <React.Fragment key={u.id}>
                        <tr className="border-b border-white/5 hover:bg-white/5 transition-colors">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-white text-xs font-bold">
                                {u.name?.[0]?.toUpperCase() || '?'}
                              </div>
                              <div>
                                <p className="text-white text-sm font-medium">{u.name}</p>
                                <p className="text-slate-500 text-xs">{u.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium ${roleColor}`}>
                              <RoleIcon className="h-3 w-3" />{u.role?.replace('_', ' ')}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-md text-xs font-medium ${statusColor}`}>{vStatus}</span>
                          </td>
                          <td className="px-4 py-3 text-slate-300 text-sm capitalize">{u.membership_tier || 'free'}</td>
                          <td className="px-4 py-3 text-slate-400 text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-1">
                              {!u.is_admin && vStatus !== 'approved' && (
                                <button onClick={() => handleQuickApprove(u.id, u.name)} disabled={approvingUserId === u.id}
                                  className="p-1.5 text-slate-400 hover:text-green-400 rounded transition-colors disabled:opacity-50" title="Quick Approve" data-testid={`quick-approve-${u.id}`}>
                                  {approvingUserId === u.id ? <RefreshCw className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                                </button>
                              )}
                              <button onClick={() => setExpandedUser(isExpanded ? null : u.id)} className="p-1.5 text-slate-400 hover:text-white rounded transition-colors" data-testid={`expand-user-${u.id}`}>
                                {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                              </button>
                              <button onClick={() => setEditingUser(u)} className="p-1.5 text-slate-400 hover:text-blue-400 rounded transition-colors" data-testid={`edit-user-${u.id}`}>
                                <Edit3 className="h-4 w-4" />
                              </button>
                              {!u.is_admin && (
                                <button onClick={() => handleDeleteUser(u.id, u.email)} className="p-1.5 text-slate-400 hover:text-red-400 rounded transition-colors" data-testid={`delete-user-${u.id}`}>
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={6} className="px-4 py-4 bg-white/[0.02]">
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                <div><p className="text-slate-500 text-xs mb-1">Phone</p><p className="text-white">{u.profile?.phone || 'Not set'}</p></div>
                                <div><p className="text-slate-500 text-xs mb-1">Location</p><p className="text-white">{u.profile?.location || 'Not set'}</p></div>
                                <div><p className="text-slate-500 text-xs mb-1">Referral Code</p><p className="text-cyan-400 font-mono text-xs">{u.profile?.referral_code || '-'}</p></div>
                                <div><p className="text-slate-500 text-xs mb-1">User ID</p><p className="text-slate-400 font-mono text-xs truncate">{u.id}</p></div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 border-t border-white/10 text-slate-400 text-xs">
              Showing {filteredUsers.length} of {users.length} users
            </div>
          </div>
        </>)}

        {/* ===== DOCUMENTS TAB ===== */}
        {activeTab === 'documents' && (
          <DocumentReviewSection documents={documents} docsLoading={docsLoading} onReviewDocument={handleReviewDocument} />
        )}

        {/* ===== NOTIFICATIONS TAB ===== */}
        {activeTab === 'notifications' && (
          <NotificationSection
            notifications={notifications}
            archivedNotifs={archivedNotifs}
            notifLoading={notifLoading}
            unreadCount={unreadCount}
            onMarkRead={handleMarkRead}
          />
        )}

        {/* ===== COMMUNITY TAB ===== */}
        {activeTab === 'community' && (
          <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden" data-testid="admin-community-section">
            <div className="px-4 py-3 border-b border-white/10">
              <h3 className="text-white font-medium">Community Posts</h3>
              <p className="text-slate-500 text-xs mt-0.5">Respond to posts from Drivers, Carriers, and Dispatchers</p>
            </div>
            {/* Post Composer */}
            <div className="px-4 py-4 border-b border-white/10 bg-white/[0.02]">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-amber-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">A</div>
                <div className="flex-1">
                  <MentionInput
                    value={newPostText}
                    mentions={newPostMentions}
                    onChange={(val, men) => { setNewPostText(val); setNewPostMentions(men); }}
                    placeholder="Write a post as Admin… mention with @, paste a link"
                    rows={3}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
                    testId="admin-new-post-input"
                  />
                  {newPostMedia.length > 0 && (
                    <div className="flex gap-2 mt-2 flex-wrap">
                      {newPostMedia.map((m, i) => (
                        <div key={i} className="relative group">
                          {m.media_type === 'video' ? (
                            <div className="w-20 h-20 rounded-lg bg-slate-900 flex items-center justify-center border border-white/10">
                              <Film className="h-6 w-6 text-white" />
                            </div>
                          ) : (
                            <img src={m.preview || mediaUrl(m.url)} alt="" className="w-20 h-20 rounded-lg object-cover" />
                          )}
                          <button onClick={() => removeAdminMedia(i)} className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center" data-testid={`admin-remove-media-${i}`}>
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center gap-2">
                      <input ref={adminFileInputRef} type="file" accept="image/*,video/*" multiple onChange={handleAdminFileSelect} className="hidden" data-testid="admin-media-input" />
                      <button onClick={() => adminFileInputRef.current?.click()} disabled={adminUploading || newPostMedia.length >= 4}
                        className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white transition-colors disabled:opacity-40" data-testid="admin-attach-media-btn">
                        {adminUploading ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />}
                        Photo / Video
                      </button>
                      <span className="text-[10px] text-slate-500">{newPostMedia.length}/4</span>
                    </div>
                    <button
                      onClick={handleCreatePost}
                      disabled={(!newPostText.trim() && newPostMedia.length === 0) || postingNew}
                      className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="admin-publish-post-btn"
                    >
                      {postingNew ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                      Publish
                    </button>
                  </div>
                </div>
              </div>
            </div>
            {communityLoading ? (
              <div className="flex items-center justify-center py-12"><RefreshCw className="h-6 w-6 animate-spin text-slate-400" /></div>
            ) : communityPosts.length === 0 ? (
              <div className="text-center py-12">
                <MessageSquare className="h-10 w-10 text-slate-600 mx-auto mb-2" />
                <p className="text-slate-400 text-sm">No community posts yet</p>
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {communityPosts.map(post => {
                  const RoleIcon = ROLE_ICONS[post.author_role] || Users;
                  const roleColor = ROLE_COLORS[post.author_role] || 'text-slate-600 bg-slate-50';
                  return (
                    <div key={post.id} className="px-4 py-4" data-testid={`community-post-${post.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 flex-1">
                          <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                            {post.author_name?.[0]?.toUpperCase() || '?'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-white text-sm font-medium">{post.author_name}</span>
                              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${roleColor}`}>
                                <RoleIcon className="h-2.5 w-2.5" />{post.author_role?.replace('_', ' ')}
                              </span>
                              <span className="text-slate-500 text-xs">{new Date(post.created_at).toLocaleString()}</span>
                            </div>
                            <div className="text-slate-300 text-sm"><PostContent text={post.content} className="!text-slate-300" /></div>
                            <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                              <span>{post.like_count || 0} likes</span>
                              <span>{post.comment_count || 0} comments</span>
                            </div>
                            {/* Comments */}
                            {post.comments?.length > 0 && (
                              <div className="mt-3 space-y-2 pl-4 border-l border-white/10">
                                {post.comments.map(c => (
                                  <div key={c.id} className={`text-xs ${c.is_admin_reply ? 'bg-blue-500/10 border border-blue-500/20 rounded-lg p-2' : ''}`}>
                                    <span className={`font-medium ${c.is_admin_reply ? 'text-blue-400' : 'text-slate-400'}`}>
                                      {c.author_name}{c.is_admin_reply && ' (Admin)'}
                                    </span>
                                    <div className="text-slate-500 ml-2 inline-block"><PostContent text={c.text} className="!text-slate-500 !text-xs inline" /></div>
                                  </div>
                                ))}
                              </div>
                            )}
                            {/* Admin Reply */}
                            <div className="mt-3 flex gap-2">
                              <input
                                type="text"
                                placeholder="Reply as admin..."
                                value={replyText[post.id] || ''}
                                onChange={(e) => setReplyText(prev => ({ ...prev, [post.id]: e.target.value }))}
                                onKeyDown={(e) => e.key === 'Enter' && handleAdminReply(post.id)}
                                className="flex-1 px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                data-testid={`admin-reply-input-${post.id}`}
                              />
                              <button onClick={() => handleAdminReply(post.id)}
                                className="p-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                                data-testid={`admin-reply-btn-${post.id}`}>
                                <Send className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                        <button onClick={() => handleDeletePost(post.id)} className="p-1.5 text-slate-400 hover:text-red-400 rounded transition-colors flex-shrink-0" title="Delete post"
                          data-testid={`delete-post-${post.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="px-4 py-3 border-t border-white/10 text-slate-400 text-xs">{communityPosts.length} posts</div>
          </div>
        )}

        {/* ===== RESOURCES TAB ===== */}
        {activeTab === 'resources' && <ResourceManagementSection />}
        {activeTab === 'affiliate' && <AffiliateApplicationsSection />}
        {activeTab === 'service_provider' && <ServiceProviderApplicationsSection />}
      </div>

      {/* Create User Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
              className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-md p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-white">Create New User</h3>
                <button onClick={() => setShowCreateModal(false)} className="text-slate-400 hover:text-white"><X className="h-5 w-5" /></button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Full Name</label>
                  <input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                    className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="John Doe" data-testid="new-user-name" />
                </div>
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Email</label>
                  <input value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="user@email.com" data-testid="new-user-email" />
                </div>
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Password</label>
                  <input value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="new-user-password" />
                </div>
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Role</label>
                  <select value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                    className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="new-user-role">
                    <option value="driver" className="bg-slate-900">Driver</option>
                    <option value="carrier_owner" className="bg-slate-900">Carrier Owner</option>
                    <option value="dispatcher" className="bg-slate-900">Dispatcher</option>
                    <option value="learner" className="bg-slate-900">Learn the Trade</option>
                    <option value="affiliate" className="bg-slate-900">Affiliate Hub</option>
                    <option value="service_provider" className="bg-slate-900">Service Provider</option>
                  </select>
                </div>
                <button onClick={handleCreateUser}
                  className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2" data-testid="submit-create-user">
                  <Plus className="h-4 w-4" />Create User
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit User Modal */}
      <AnimatePresence>
        {editingUser && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
              className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-md p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-white">Edit User</h3>
                <button onClick={() => setEditingUser(null)} className="text-slate-400 hover:text-white"><X className="h-5 w-5" /></button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Name</label>
                  <input value={editingUser.name || ''} onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                    className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="edit-user-name" />
                </div>
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Role</label>
                  <select value={editingUser.role || 'driver'} onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                    className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="edit-user-role">
                    <option value="driver" className="bg-slate-900">Driver</option>
                    <option value="carrier_owner" className="bg-slate-900">Carrier Owner</option>
                    <option value="dispatcher" className="bg-slate-900">Dispatcher</option>
                    <option value="learner" className="bg-slate-900">Learn the Trade</option>
                    <option value="affiliate" className="bg-slate-900">Affiliate Hub</option>
                    <option value="service_provider" className="bg-slate-900">Service Provider</option>
                    <option value="admin" className="bg-slate-900">Admin</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Verification Status</label>
                  <select value={editingUser.profile?.verification_status || 'pending'} onChange={(e) => setEditingUser({ ...editingUser, verification_status: e.target.value })}
                    className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="edit-user-status">
                    <option value="pending" className="bg-slate-900">Pending</option>
                    <option value="approved" className="bg-slate-900">Approved</option>
                    <option value="declined" className="bg-slate-900">Declined</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Membership Tier</label>
                  <select value={editingUser.membership_tier || 'free'} onChange={(e) => setEditingUser({ ...editingUser, membership_tier: e.target.value })}
                    className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="edit-user-tier">
                    <option value="free" className="bg-slate-900">Free</option>
                    <option value="starter" className="bg-slate-900">Starter</option>
                    <option value="professional" className="bg-slate-900">Professional</option>
                    <option value="enterprise" className="bg-slate-900">Enterprise</option>
                  </select>
                </div>
                <button onClick={() => handleUpdateUser(editingUser.id, {
                  name: editingUser.name, role: editingUser.role, membership_tier: editingUser.membership_tier,
                  verification_status: editingUser.verification_status || editingUser.profile?.verification_status
                })}
                  className="w-full py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2" data-testid="submit-edit-user">
                  <Check className="h-4 w-4" />Save Changes
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminDashboard;
